import { AbstractViewModel, useViewModel } from '@footsteps/shared';
import type { WozCaseAttachment } from '../woz-case-manager/woz-case/woz-case.types';
import type { AttachmentItemState } from './attachment.state';
import type { AttachmentItemDeps } from './attachment.types';

export class AttachmentItemViewModel extends AbstractViewModel<AttachmentItemState> {
  private readonly attachment: WozCaseAttachment;
  private readonly deps: AttachmentItemDeps;

  constructor(attachment: WozCaseAttachment, deps: AttachmentItemDeps) {
    super({
      deleteConfirmOpen: false,
      isEditingDescription: false,
      descriptionValue: attachment.description ?? '',
    });
    this.attachment = attachment;
    this.deps = deps;
  }

  get isImage(): boolean {
    return this.attachment.type === 'image';
  }

  get canEditDescription(): boolean {
    return !!this.deps.onUpdateDescription;
  }

  get imageDimensions(): string {
    if (!this.isImage || !this.attachment.imageWidth || !this.attachment.imageHeight) return '';
    return ` · ${this.attachment.imageWidth}×${this.attachment.imageHeight}`;
  }

  openDeleteConfirm = (): void => {
    this.update((state) => {
      state.deleteConfirmOpen = true;
    });
  };

  closeDeleteConfirm = (): void => {
    this.update((state) => {
      state.deleteConfirmOpen = false;
    });
  };

  confirmDelete = async (): Promise<void> => {
    await this.deps.onDelete(this.attachment.id);
    this.update((state) => {
      state.deleteConfirmOpen = false;
    });
  };

  download = async (): Promise<void> => {
    const url = await this.deps.onGetSignedUrl(this.attachment.id);
    window.open(url, '_blank');
  };

  startEditDescription = (): void => {
    this.update((state) => {
      state.isEditingDescription = true;
    });
  };

  setDescriptionValue = (value: string): void => {
    this.update((state) => {
      state.descriptionValue = value;
    });
  };

  saveDescription = (): void => {
    const { descriptionValue } = this.state.getValue();
    this.deps.onUpdateDescription?.(this.attachment.id, descriptionValue);
    this.update((state) => {
      state.isEditingDescription = false;
    });
  };

  cancelEditDescription = (): void => {
    this.update((state) => {
      state.descriptionValue = this.attachment.description ?? '';
      state.isEditingDescription = false;
    });
  };

  handleDescriptionKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') this.saveDescription();
    if (event.key === 'Escape') this.cancelEditDescription();
  };
}

export function useAttachmentItemViewModel(attachment: WozCaseAttachment, deps: AttachmentItemDeps) {
  return useViewModel<AttachmentItemState, AttachmentItemViewModel>(
    () => new AttachmentItemViewModel(attachment, deps)
  );
}
